import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { RiskFactorInputTabs } from '@/components/shared/RiskFactorInputTabs';
import { useRiskFactors } from '../hooks';
import { paperStyles } from './styles';
import {
  isTemplateFieldDataUpdated,
  selectedRiskAssessmentFilters,
  maintainSelectedEquipments,
  selectedAssemblyIds,
  unselectedAssemblyIds,
} from '@/components/Analysis/state';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import useDialogState from '../hooks/useDialogState';
import * as riskState from '../state/atoms';
import * as analysisState from '@/components/Analysis/state';
import { editorStyles } from '../ProbabilityOfFailure/styles';
import { CalculateMenu, CalculateMenuRef } from '../shared/CalculateMenu';
import { BULK_COMMENTS_FEATURE_FLAG } from '@/constants';
import {
  GetVersionsDocument,
  GetAssembliesByRiskAssessmentFiltersDocument,
  useBulkCreateCommentsForAssembliesMutation,
  useUpdateRiskAssessmentTemplateFieldDataForAssembliesMutation,
} from '@/__generated__/graphql';
import { ActionButton } from '../shared/styles';
import { CircularProgress } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

type ConsequenceOfFailureProps = {
  updateAllAssemblies: boolean;
  selected: boolean;
};

export const ConsequenceOfFailure = ({
  updateAllAssemblies,
  selected,
}: ConsequenceOfFailureProps) => {
  const [selectedInputs, setSelectedInputs] = useState<
    { [key: string]: string | null } | undefined
  >();
  const setMaintainSelections = useSetAtom(maintainSelectedEquipments);
  const selectedFilters = useAtomValue(selectedRiskAssessmentFilters);
  const assemblyIds = useAtomValue(selectedAssemblyIds);
  const excludedAssemblyIds = useAtomValue(unselectedAssemblyIds);
  const suppressMissingCommentsCheck = useAtomValue(riskState.suppressMissingCommentsCheck);
  const setIsTemplateFieldDataUpdated = useSetAtom(isTemplateFieldDataUpdated);
  const setEditorExpanded = useSetAtom(riskState.editorExpanded);
  const setShowBulkCommentsDialog = useSetAtom(riskState.showBulkCommentsDialog);
  const [isBulkCommentsUpdateRequested, setIsBulkCommentsUpdateRequested] = useAtom(
    riskState.isBulkCommentsUpdateRequested
  );
  const setShowMissingCommentsDialog = useSetAtom(riskState.showMissingCommentsDialog);
  const [ignoreMissingComments, setIgnoreMissingComments] = useAtom(
    riskState.ignoreMissingComments
  );
  const { templateId, structureId, calculatedValues } = useRiskFactors(selectedInputs);
  const bulkComments = useAtomValue(riskState.cofCommentsState);
  const structureInfo = useAtomValue(analysisState.structureFeatures);

  const calculateMenuRef = useRef<CalculateMenuRef>(null);

  const { resetDialogState, resetCommentState } = useDialogState();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const riskFeatures: string[] = useMemo(() => {
    if (!structureInfo?.features?.riskAssessment) return [];
    return (
      (Array.isArray(structureInfo?.features?.riskAssessment) &&
        structureInfo?.features?.riskAssessment) ??
      []
    );
  }, [structureInfo?.features?.riskAssessment]);

  const [bulkCreateCommentsForAssemblies, { loading: loadingComments }] =
    useBulkCreateCommentsForAssembliesMutation();
  const [updateAssembliesRiskFactors, { loading }] =
    useUpdateRiskAssessmentTemplateFieldDataForAssembliesMutation({
      variables: {
        input: {
          structureId,
          templateId,
          assemblyIds,
          excludedAssemblyIds,
          selectedInputs: calculatedValues,
          selectedFilters,
          updateAllAssemblies,
          lastModifiedFor: 'COF calculation',
        },
      },
      refetchQueries: [GetAssembliesByRiskAssessmentFiltersDocument, GetVersionsDocument],
      awaitRefetchQueries: true,
      onCompleted: () => {
        showSnackBar('The COF table has been updated successfully', 'success');
        setSelectedInputs(undefined);
        setIsTemplateFieldDataUpdated(true);
        setEditorExpanded(false);
      },
    });

  const handleSelectedValues = useCallback(
    (selectedValues: { [key: string]: string | null }) => {
      setSelectedInputs(selectedValues);
    },
    [setSelectedInputs]
  );

  const handleClick = useCallback(() => {
    const commentsExist =
      bulkComments?.comments &&
      bulkComments?.comments.length > 0 &&
      bulkComments.comments.some((comment) => comment.text !== '');
    if (!suppressMissingCommentsCheck && !commentsExist && !ignoreMissingComments) {
      setShowMissingCommentsDialog(true);
      return;
    }
    calculateMenuRef.current?.handleClose();
    setMaintainSelections(true);
    updateAssembliesRiskFactors();
    if (bulkComments?.comments && bulkComments?.comments.length > 0)
      bulkCreateCommentsForAssemblies({
        variables: {
          input: {
            structureId,
            referenceIds: assemblyIds,
            excludedAssemblyIds,
            selectedFilters,
            comments: bulkComments?.comments,
            collectionName: 'assembly',
            updateAllAssemblies,
            clearPreviousComments: true,
          },
        },
      });
    resetDialogState();
    resetCommentState();
    setIgnoreMissingComments(false);
  }, [
    bulkComments?.comments,
    suppressMissingCommentsCheck,
    ignoreMissingComments,
    setMaintainSelections,
    updateAssembliesRiskFactors,
    bulkCreateCommentsForAssemblies,
    structureId,
    assemblyIds,
    excludedAssemblyIds,
    selectedFilters,
    updateAllAssemblies,
    resetDialogState,
    resetCommentState,
    setIgnoreMissingComments,
    setShowMissingCommentsDialog,
  ]);

  const disableBulkComments = useMemo(() => {
    return !(
      bulkComments?.comments &&
      bulkComments?.comments.length > 0 &&
      bulkComments.comments.some((comment) => comment.text !== '')
    );
  }, [bulkComments?.comments]);

  const handleBulkComments = useCallback(() => {
    if (
      !isBulkCommentsUpdateRequested &&
      calculatedValues.length > 0 &&
      calculatedValues.some((calculated) => calculated.value !== '')
    ) {
      setShowBulkCommentsDialog(true);
      return;
    }
    setMaintainSelections(true);
    calculateMenuRef.current?.handleClose();
    if (bulkComments?.comments && bulkComments?.comments.length > 0)
      bulkCreateCommentsForAssemblies({
        variables: {
          input: {
            structureId,
            referenceIds: assemblyIds,
            excludedAssemblyIds,
            selectedFilters,
            comments: bulkComments?.comments,
            collectionName: 'assembly',
            updateAllAssemblies,
            clearPreviousComments: true,
          },
        },
        onCompleted: () => {
          showSnackBar('The comments have been updated successfully!', 'success');
          setSelectedInputs(undefined);
          setIsTemplateFieldDataUpdated(true);
          setEditorExpanded(false);
        },
      });
    setIsBulkCommentsUpdateRequested(false);
    resetDialogState();
    resetCommentState();
  }, [
    calculatedValues,
    setMaintainSelections,
    bulkComments?.comments,
    bulkCreateCommentsForAssemblies,
    structureId,
    assemblyIds,
    excludedAssemblyIds,
    selectedFilters,
    updateAllAssemblies,
    resetDialogState,
    resetCommentState,
    isBulkCommentsUpdateRequested,
    setShowBulkCommentsDialog,
    showSnackBar,
    setIsTemplateFieldDataUpdated,
    setEditorExpanded,
    setIsBulkCommentsUpdateRequested,
  ]);

  useEffect(() => {
    if (ignoreMissingComments && selected) handleClick();
  }, [handleClick, ignoreMissingComments, selected]);

  useEffect(() => {
    if (isBulkCommentsUpdateRequested && selected) handleBulkComments();
  }, [handleBulkComments, isBulkCommentsUpdateRequested, selected]);

  return (
    <Paper
      data-testid="consequence-of-failure"
      elevation={0}
      sx={{ ...paperStyles, display: selected ? 'block' : 'none' }}
    >
      <Stack spacing={2} sx={editorStyles}>
        <RiskFactorInputTabs parentTab="cof" onInputChange={handleSelectedValues} />
      </Stack>
      <Stack alignItems="flex-end" mt={2}>
        {riskFeatures.includes(BULK_COMMENTS_FEATURE_FLAG) ? (
          <CalculateMenu
            ref={calculateMenuRef}
            onCalculate={handleClick}
            onUpdateComments={handleBulkComments}
            disableComments={disableBulkComments}
            disabled={loading}
            loading={loading}
            loadingComments={loadingComments}
          />
        ) : (
          <ActionButton contained disabled={loading} onClick={handleClick}>
            <>
              {loading ? 'Calculating...' : 'Calculate & Update Table'}
              {loading && <CircularProgress size={20} />}
            </>
          </ActionButton>
        )}
      </Stack>
    </Paper>
  );
};
